import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Search, Loader2, MapPin, Info } from 'lucide-react';
import { Input } from './ui/input';

// Fix leaflet default icon paths for webpack bundling
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const orangeIcon = new L.DivIcon({
  className: '',
  html: `
    <div style="position:relative;">
      <div style="width:32px;height:32px;background:#f97316;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 4px 12px rgba(249,115,22,0.5);"></div>
      <div style="position:absolute;top:8px;left:8px;width:12px;height:12px;background:#fff;border-radius:50%;"></div>
    </div>
  `,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
});

const Recenter = ({ position, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, zoom || map.getZoom(), { duration: 0.6 });
  }, [position, zoom, map]);
  return null;
};

const MapClickHandler = ({ onChange }) => {
  useMapEvents({
    click(e) {
      onChange({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
};

import api from '../lib/api';

// Photon search (via backend proxy) — faster autocomplete than raw Nominatim
const searchAddress = async (query) => {
  if (!query || query.length < 2) return [];
  const q = query.trim();
  const isZip = /^\d{5}$/.test(q);
  try {
    const params = isZip ? { postalcode: q } : { q };
    const r = await api.get('/geocode/search', { params });
    let data = r.data || [];
    if (isZip && data.length === 0) {
      const alt = await api.get('/geocode/search', { params: { q: `${q} Mexico` } });
      data = alt.data || [];
    }
    return data.map((d) => {
      const addr = d.address || {};
      const line1 = [addr.road, addr.house_number].filter(Boolean).join(' ');
      const line2 = [addr.suburb, addr.city, addr.state, addr.postcode].filter(Boolean).join(', ');
      return {
        lat: parseFloat(d.lat),
        lng: parseFloat(d.lon),
        label: d.display_name,
        line1: line1 || d.display_name?.split(',')[0] || d.display_name,
        line2: line2 || d.display_name?.split(',').slice(1).join(',').trim(),
        zip: addr.postcode,
        details: addr,
      };
    });
  } catch { return []; }
};

// Reverse geocoding proxied through backend
const reverseGeocode = async (lat, lng) => {
  try {
    const r = await api.get('/geocode/reverse', { params: { lat, lng } });
    const d = r.data;
    if (!d) return null;
    return {
      label: d.display_name,
      zip: d.address?.postcode,
      details: d.address || {},
    };
  } catch { return null; }
};

const AddressMapPicker = ({
  value,
  onChange,
  zones = [],
  height = 340,
  interactive = true,
  showZones = true,
  hideSearch = false,
}) => {
  const [q, setQ] = useState('');
  const [sugs, setSugs] = useState([]);
  const [showSugs, setShowSugs] = useState(false);
  const [searching, setSearching] = useState(false);
  const timer = useRef(null);
  const containerRef = useRef(null);
  const defaultCenter = value?.lat ? [value.lat, value.lng] : [25.6866, -100.3161]; // Zona Metropolitana de Monterrey

  useEffect(() => {
    if (value?.address && value.address !== q) setQ(value.address);
    if (!value) setQ('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.address]);

  useEffect(() => {
    if (!q || q === value?.address) { setSugs([]); return; }
    if (q.length < 2) { setSugs([]); return; }
    clearTimeout(timer.current);
    setSearching(true);
    timer.current = setTimeout(async () => {
      const results = await searchAddress(q);
      setSugs(results);
      setSearching(false);
      setShowSugs(true);
    }, 200);
    return () => clearTimeout(timer.current);
  }, [q, value?.address]);

  useEffect(() => {
    const onClickOut = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setShowSugs(false);
    };
    document.addEventListener('mousedown', onClickOut);
    return () => document.removeEventListener('mousedown', onClickOut);
  }, []);

  const pickSug = (s) => {
    setShowSugs(false);
    setQ(s.label);
    onChange({ lat: s.lat, lng: s.lng, address: s.label, zip: s.zip, details: s.details });
  };

  const onMapChange = useCallback(async ({ lat, lng }) => {
    onChange({ lat, lng, address: q, zip: value?.zip });
    // reverse-geocode to update address text
    const r = await reverseGeocode(lat, lng);
    if (r) {
      setQ(r.label);
      onChange({ lat, lng, address: r.label, zip: r.zip, details: r.details });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChange]);

  return (
    <div className="space-y-3">
      {interactive && !hideSearch && (
        <div className="relative" ref={containerRef}>
          <Search className="w-4 h-4 text-orange-500 absolute left-4 top-1/2 -translate-y-1/2 z-10" />
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); }}
            onFocus={() => q && sugs.length > 0 && setShowSugs(true)}
            placeholder="Escribe tu dirección o código postal"
            className="pl-11 pr-10 h-12 bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-orange-500/50"
          />
          {searching && <Loader2 className="w-4 h-4 text-white/60 absolute right-3 top-1/2 -translate-y-1/2 animate-spin" />}

          {showSugs && sugs.length > 0 && (
            <div className="absolute z-[1000] top-full left-0 right-0 mt-1 bg-neutral-950 border border-white/10 rounded-xl shadow-2xl overflow-hidden">
              {sugs.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => pickSug(s)}
                  data-testid={`address-sug-${i}`}
                  className="w-full text-left px-4 py-2.5 hover:bg-orange-500/10 border-b border-white/5 last:border-0 flex items-start gap-3 group"
                >
                  <div className="w-8 h-8 rounded-lg bg-orange-500/15 border border-orange-500/30 text-orange-400 flex items-center justify-center shrink-0 mt-0.5 group-hover:bg-orange-500 group-hover:text-black transition-colors">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm font-semibold truncate">{s.line1}</p>
                    {s.line2 && <p className="text-white/50 text-xs truncate">{s.line2}</p>}
                  </div>
                </button>
              ))}
            </div>
          )}
          {showSugs && !searching && q.length >= 2 && sugs.length === 0 && (
            <div className="absolute z-[1000] top-full left-0 right-0 mt-1 bg-neutral-950 border border-white/10 rounded-xl shadow-2xl overflow-hidden px-4 py-3">
              <p className="text-white/60 text-sm">Sin resultados. Prueba con calle, colonia o código postal.</p>
            </div>
          )}
        </div>
      )}

      {interactive && !hideSearch && (
        <div className="rounded-xl bg-white/5 border border-white/10 p-3 flex items-start gap-2 text-xs text-white/70">
          <Info className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
          <span>Si es necesario, arrastra el marcador o haz clic en el mapa para ajustar tu ubicación exacta. 📍</span>
        </div>
      )}

      <div className="rounded-xl overflow-hidden border border-white/10 bg-neutral-900" style={{ height }}>
        <MapContainer
          center={defaultCenter}
          zoom={value?.lat ? 14 : 11}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={interactive}
        >
          <TileLayer
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap'
          />
          {showZones && zones.map((z) => (
            <Circle
              key={z.id}
              center={[z.lat, z.lng]}
              radius={z.radius_meters || 5000}
              pathOptions={{
                color: z.color || '#f97316',
                fillColor: z.color || '#f97316',
                fillOpacity: 0.15,
                weight: 2,
              }}
            />
          ))}
          {value?.lat && (
            <Marker
              position={[value.lat, value.lng]}
              icon={orangeIcon}
              draggable={interactive}
              eventHandlers={{
                dragend: (e) => {
                  const ll = e.target.getLatLng();
                  onMapChange({ lat: ll.lat, lng: ll.lng });
                },
              }}
            />
          )}
          <Recenter position={value?.lat ? [value.lat, value.lng] : null} zoom={14} />
          {interactive && <MapClickHandler onChange={onMapChange} />}
        </MapContainer>
      </div>
    </div>
  );
};

export default AddressMapPicker;
